/**
 * Whether an element is on screen, for the кружочки that play by themselves.
 *
 * The point of asking is not politeness about CPU: it is that a кружочек keeps
 * `preload="none"`, so a note nobody has scrolled to fetches no bytes at all.
 * Playback is what triggers the download, and playback only starts here.
 *
 * A threshold rather than "any pixel showing" because half a circle poking past
 * the top of the thread is not something anyone is watching.
 */
import { useEffect, useState } from 'react';

export function useInView(element: Element | null, threshold = 0.35): boolean {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!element) {
      // A remounted bubble starts over rather than inheriting the last
      // element's answer, which would claim a note is visible before anything
      // has looked.
      setInView(false);
      return;
    }
    // No IntersectionObserver means no way to tell, and the honest fallback for
    // a silent autoplaying circle is to play: a thread of frozen frames is a
    // worse failure than a thread that decodes more than it needs to.
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setInView(entry.isIntersecting);
      },
      { threshold },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, threshold]);

  return inView;
}
